import {
  useSitesQuery,
  useSiteStatsQuery,
} from '@/remote/site-management/fetch-sites';
import type { Site } from '@/types/site';
import { useMemo, useState } from 'react';

// Site 타입을 SiteData 타입으로 변환하는 함수
const transformSiteToSiteData = (site: Site) => {
  return {
    id: parseInt(site.id) || 0,
    siteId: site.siteId,
    name: site.name,
    managementNumber: site.managementNumber,
    cdmaNumber: site.cdmaNumber,
    status: '',
    address: site.wellPosition?.address || '주소 없음',
    temperature: '',
    motorOperation: '',
    motorRunningTime: 0,
    motorStoppedTime: 0,
    cumulativeHours: 0,
    cumulativeHoursToday: 0,
    waterLevel: 0,
    cumulativeFlow: '',
    installationDate:
      site.installedDate || new Date().toISOString().split('T')[0],
    lastInspection: '',
    waterLevelTrend: {
      today: [],
      yesterday: [],
      week: [],
    },
    alias: site.alias,
    motorActivateCount: site.motorActivateCount,
  };
};

export function useSiteData() {
  const [searchTerm, setSearchTerm] = useState('');

  // 사이트 데이터 가져오기
  const { data: sitesData = [], error, isLoading, refetch } = useSitesQuery();

  // 사이트 통계 데이터 가져오기
  const { data: statsData } = useSiteStatsQuery();

  // Site[] -> SiteData[] 변환
  const transformedSites = useMemo(() => {
    if (!sitesData) return [];
    return sitesData.map(transformSiteToSiteData);
  }, [sitesData]);

  // 필터링된 사이트 데이터
  const filteredSites = useMemo(() => {
    if (!transformedSites.length || !sitesData.length) return [];

    const searchLower = searchTerm.toLowerCase();
    // 하이픈을 제거한 검색어 (숫자만)
    const searchNumbers = searchTerm.replace(/[-\s]/g, '');

    return transformedSites.filter((site, index) => {
      const originalSite = sitesData[index]; // 원본 사이트 데이터에서 CDMA 번호 가져오기

      return (
        site?.managementNumber?.toLowerCase().includes(searchLower) ||
        site.name.toLowerCase().includes(searchLower) ||
        site.address.toLowerCase().includes(searchLower) ||
        site.siteId.toLowerCase().includes(searchLower) ||
        // CDMA 번호 검색 (원본 형태)
        originalSite.cdmaNumber?.toLowerCase().includes(searchLower) ||
        // CDMA 번호 검색 (하이픈 제거된 형태)
        (originalSite.cdmaNumber
          ?.replace(/[-\s]/g, '')
          .includes(searchNumbers) &&
          searchNumbers.length > 0)
      );
    });
  }, [transformedSites, sitesData, searchTerm]);

  // 새로고침 함수
  const refreshData = () => {
    refetch();
  };

  return {
    searchTerm,
    setSearchTerm,
    filteredSites,
    refreshData,

    // 추가 상태들
    isLoading,
    error,
    stats: statsData,
    totalSites: transformedSites.length,
  };
}
